import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, CopyPlus, Layers, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DataTable } from '@/components/ui/data-table';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import {
  DueCell,
  GroupTeamCell,
  ProjectCell,
  projectMenuItems,
  StageCell,
  SystemChip,
  TeamCell,
} from './ProjectCells';
import { cleanText, projectStatusLabel } from './projectsModel';

/*
  The projects table (03 #4): ☐ | System | Project | Team | Stage | Due | Status | ⋯ with a sticky
  header, sortable Project / Stage / Due / Status, and "Group by site" rows (03 #7) — a site row
  ("Nobu Hotel · 4 systems") that opens to its projects, which hang under it on a tree line.

  rows: [{ key, kind: 'project' | 'group' | 'child', project?, group?, info?, expanded?, last? }]
  (built by the page). Selection keys are project ids plus the keys of fully selected groups.

  The table never scrolls sideways (that would stop the header sticking under the top bar): the page
  picks `density` from the room it has — 'wide' from TABLE_WIDE_MIN_WIDTH px, 'medium' (narrower
  columns, "⌛ 798 d" instead of "Awaiting approval · 798 d") from TABLE_MIN_WIDTH px, and the phone
  cards below that (tablets, small laptops).
*/

export const TABLE_MIN_WIDTH = 900;
export const TABLE_WIDE_MIN_WIDTH = 1060;

// Fixed column widths per density (+ the 40px check box column); Project takes the rest: medium leaves
// it >= 167px from TABLE_MIN_WIDTH (232 at a 1280 window), wide >= 235px from TABLE_WIDE_MIN_WIDTH.
// System is 84 in both: the widest chip ("HA ZIGB") needs it.
const WIDTHS = {
  wide: { system: 84, team: 166, stage: 288, due: 92, status: 104, actions: 48 },
  medium: { system: 84, team: 148, stage: 228, due: 88, status: 100, actions: 44 },
};

const slug = (text) => cleanText(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'site';
const systemName = (project) => cleanText(project.system_type) || 'No system type';

// Name line of a collapsed site row: 6px between name, "4 systems" and the mini chips (4px apart); the "+2" chip
// that stands for the rest takes 22px + its gap. The name keeps at least 45% of the line (96px on narrow tables).
const LINE_GAP = 6;
const CHIP_GAP = 4;
const MORE_CHIP_WIDTH = 26;
const NAME_SHARE = 0.45;
const NAME_MIN = 96;

/**
 * How many chips (their widths in px, in order) fit in `available` px: all of them, else as many as leave room
 * for the "+K" chip. 0 when not even one fits (the "4 systems" pill still says how many, its tooltip which).
 */
export function fitChipCount(widths, available, { gap = CHIP_GAP, more = MORE_CHIP_WIDTH } = {}) {
  const total = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1);
  if (total <= available) return widths.length;
  let used = more;
  let count = 0;
  while (count < widths.length && used + widths[count] + gap <= available) {
    used += widths[count] + gap;
    count += 1;
  }
  return count;
}

/**
 * Room for the chips on a site row's name line (px): the line minus the pill (0 = none on screen), the gaps and the
 * part of the name that always stays (all of a short name; at least 45% of the line / 96px of a long one).
 */
export function chipRoom({ line, name, pill = 0 }) {
  const keep = Math.min(name, Math.max(NAME_MIN, line * NAME_SHARE));
  return line - keep - (pill ? pill + LINE_GAP : 0) - LINE_GAP;
}

/**
 * "Nobu Hotel  4 systems  BMS GRMS +2": the site name, its system count and — collapsed — the systems as mini
 * chips: the ones that fit, then a muted "+2" with the rest in its tooltip, never a chip cut off or silently left
 * out. The chips may take room from a long name (it truncates, the full name is in its tooltip). `compact` (the
 * narrower table): a collapsed row gives the "4 systems" pill's room to the chips (screen readers still hear it).
 * Mount it with a key per systems list: chip widths are measured with every chip on screen.
 */
function GroupNameLine({ group, info, expanded, compact = false }) {
  const pillOnScreen = expanded || !compact;
  const lineRef = useRef(null);
  const widthsRef = useRef([]);
  const projects = group.projects;
  const [shown, setShown] = useState(projects.length);
  useLayoutEffect(() => {
    const line = lineRef.current;
    if (!line || expanded) return undefined;
    const fit = () => {
      if (!line.clientWidth) return; // not laid out (hidden, or a test without layout): keep every chip
      const name = line.querySelector('[data-part="name"]');
      const pill = line.querySelector('[data-part="count"]');
      // Chips on screen are measured again (fonts may have loaded since); hidden ones keep their width.
      line.querySelectorAll('[data-testid="project-group-systems"] [data-system]').forEach((chip, index) => {
        widthsRef.current[index] = Math.max(chip.offsetWidth, chip.scrollWidth);
      });
      if (!name || widthsRef.current.length < projects.length) return;
      const room = chipRoom({ line: line.clientWidth, name: name.scrollWidth, pill: pillOnScreen && pill ? pill.offsetWidth : 0 });
      setShown(fitChipCount(widthsRef.current, room));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(line);
    return () => observer.disconnect();
  }, [expanded, pillOnScreen, projects.length]);
  const rest = projects.slice(shown);
  return (
    <div ref={lineRef} className="flex min-w-0 items-center gap-1.5 overflow-hidden">
      <span className="min-w-0 shrink truncate text-[13.5px] font-semibold leading-[18px] text-ink" title={group.name} data-part="name">{group.name}</span>
      {pillOnScreen ? (
        <StatusPill
          tone="navy"
          label={`${info.count} systems`}
          size="sm"
          hideDot
          className="shrink-0"
          title={projects.map(systemName).join(', ')}
          data-part="count"
        />
      ) : <span className="sr-only">{info.count} systems</span>}
      {!expanded && shown > 0 ? (
        <span className="flex h-[18px] shrink-0 items-center gap-1" aria-hidden="true" data-testid="project-group-systems">
          {projects.slice(0, shown).map((project) => (
            <SystemChip key={project.id} systemType={project.system_type} size="sm" className="max-w-none" />
          ))}
          {rest.length ? (
            <span
              className="inline-grid h-[18px] min-w-[22px] shrink-0 place-items-center rounded-[5px] bg-[color:var(--status-gray-bg)] px-1 text-[10px] font-bold leading-none text-ink-2"
              title={`Also: ${rest.map(systemName).join(', ')}`}
              data-testid="project-group-systems-more"
            >
              +{rest.length}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

function GroupProjectCell({ row, compact }) {
  const { group, info, expanded } = row;
  const place = [group.client, info.placeText].filter(Boolean).join(' · ');
  const systemsKey = group.projects.map((project) => `${project.id}:${cleanText(project.system_type)}`).join('|');
  return (
    <div className="min-w-0">
      <GroupNameLine key={systemsKey} group={group} info={info} expanded={expanded} compact={compact} />
      {place || info.missingClient ? (
        <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[12px] leading-4 text-ink-3">
          {place ? <span className="min-w-0 truncate">{place}</span> : null}
          {info.missingClient ? (
            <span className="shrink-0 font-semibold text-status-amber-ink">
              {place ? <span className="font-normal text-ink-4" aria-hidden="true">· </span> : null}
              {info.missingClient} missing client
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ProjectsTable({
  rows,
  loading,
  empty,
  sort,
  onSortChange,
  selectable,
  selectedKeys,
  onSelectionChange,
  renderBulkBar,
  onToggleGroup,
  onSelectGroup,
  canEdit,
  canDelete,
  onEdit,
  onDuplicate,
  onDelete,
  onCopyLink,
  now,
  pagingKey,
  className,
  density = 'wide',
}) {
  const navigate = useNavigate();
  const selected = useMemo(() => new Set(selectedKeys || []), [selectedKeys]);
  const medium = density === 'medium';

  const columns = useMemo(() => {
    const menuFor = (project) => projectMenuItems(project, {
      navigate, canEdit, canDelete, onEdit, onDuplicate, onDelete, onCopyLink,
    });
    const widths = WIDTHS[medium ? 'medium' : 'wide'];
    // Child rows: 40px, a quiet background; group rows keep the table's colours. Every cell is
    // `relative`: tree lines and screen-reader-only text (position: absolute) stay inside their cell.
    const rowCell = (row, ...extra) => cn(
      'relative',
      row.kind === 'child' ? 'h-10 py-1' : 'h-[52px]',
      row.kind === 'child' && !selected.has(row.key) && 'bg-[color:var(--surface-2)]',
      ...extra
    );
    return [
      {
        key: 'system',
        header: 'System',
        width: widths.system,
        hideable: false,
        cellClassName: (row) => rowCell(row, row.kind === 'child' && 'pr-0'),
        render: (row) => {
          if (row.kind === 'group') {
            return (
              <>
                <button
                  type="button"
                  onClick={() => onToggleGroup(row.group.key)}
                  aria-expanded={row.expanded}
                  aria-label={`${row.expanded ? 'Hide' : 'Show'} the ${row.info.count} projects of ${row.group.name}`}
                  className="flex items-center gap-1 rounded-md px-0.5 py-0.5 text-brand-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="project-group-toggle"
                >
                  {row.expanded
                    ? <ChevronDown className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" />
                    : <ChevronRight className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" />}
                  <Layers className="h-4 w-4" aria-hidden="true" />
                </button>
                {row.expanded ? <span className="absolute bottom-0 left-[21px] top-[34px] w-px bg-navy-300" aria-hidden="true" /> : null}
              </>
            );
          }
          if (row.kind === 'child') {
            return (
              <>
                <span className={cn('absolute left-[21px] top-0 w-px bg-navy-300', row.last ? 'h-1/2' : 'bottom-0')} aria-hidden="true" />
                <span className="absolute left-[21px] top-1/2 h-px w-2 bg-navy-300" aria-hidden="true" />
                <span className="block pl-[18px]">
                  <SystemChip systemType={row.project.system_type} size="sm" className="min-w-[34px] max-w-[60px]" />
                </span>
              </>
            );
          }
          return <SystemChip systemType={row.project.system_type} />;
        },
      },
      {
        key: 'name',
        header: 'Project',
        sortable: true,
        hideable: false,
        cellClassName: (row) => rowCell(row, 'max-w-0'),
        render: (row) => {
          if (row.kind === 'group') return <GroupProjectCell row={row} compact={medium} />;
          if (row.kind === 'child') {
            const clientMissing = row.group.client && !cleanText(row.project.client);
            return (
              <ProjectCell
                project={row.project}
                onEdit={canEdit ? onEdit : undefined}
                now={now}
                compact
                note={clientMissing ? <span className="font-semibold text-status-amber-ink">Client missing · grouped by name</span> : null}
              />
            );
          }
          return <ProjectCell project={row.project} onEdit={canEdit ? onEdit : undefined} now={now} shortSetup={medium} />;
        },
      },
      {
        key: 'team',
        header: 'Team',
        width: widths.team,
        cellClassName: (row) => rowCell(row, 'max-w-0'),
        render: (row) => {
          if (row.kind === 'group') return <GroupTeamCell info={row.info} />;
          return <TeamCell project={row.project} onAssign={canEdit ? onEdit : undefined} compact={row.kind === 'child'} />;
        },
      },
      {
        key: 'stage',
        header: 'Stage',
        width: widths.stage,
        sortable: true,
        cellClassName: (row) => rowCell(row, 'max-w-0'),
        render: (row) => {
          if (row.kind === 'group') {
            return row.info.earliest
              ? (
                <StageCell
                  project={row.info.earliest}
                  // Narrower table: "earliest" alone, so the stage name keeps its room.
                  note={medium ? 'earliest' : `earliest of ${row.info.count}`}
                  noteTitle={`The earliest stage of the ${row.info.count} projects`}
                  awaitingFrom={row.info.longestWait}
                  shortNote={medium}
                />
              )
              : <span className="text-ink-4">—</span>;
          }
          return <StageCell project={row.project} compact={row.kind === 'child'} shortNote={medium} />;
        },
      },
      {
        key: 'due',
        header: 'Due',
        width: widths.due,
        sortable: true,
        // 03 mockup: the ↕ hint stays on "Due" so people see the list can be sorted by it.
        sortHint: true,
        cellClassName: (row) => rowCell(row),
        render: (row) => {
          if (row.kind === 'group') {
            return row.info.soonest ? <DueCell project={row.info.soonest} now={now} /> : <span className="text-ink-4" title="No end date">—</span>;
          }
          return <DueCell project={row.project} now={now} compact={row.kind === 'child'} />;
        },
      },
      {
        key: 'status',
        header: 'Status',
        width: widths.status,
        sortable: true,
        headerClassName: 'px-2',
        // "Completed" needs the full width of the column.
        cellClassName: (row) => rowCell(row, 'px-2'),
        render: (row) => {
          if (row.kind === 'group') {
            return row.info.status
              ? <StatusPill status={row.info.status} domain="project" label={projectStatusLabel(row.info.status) || undefined} size="sm" />
              : <StatusPill tone="gray" label="Mixed" size="sm" />;
          }
          // "On hold", as the tab says it (the stored value stays 'On Hold').
          return <StatusPill status={row.project.status} domain="project" label={projectStatusLabel(row.project.status) || undefined} size="sm" data-testid="project-status" />;
        },
      },
      {
        key: 'actions',
        header: <span className="sr-only">Actions</span>,
        title: 'Actions',
        width: widths.actions,
        hideable: false,
        align: 'right',
        cellClassName: (row) => rowCell(row, 'pl-0'),
        render: (row) => {
          if (row.kind === 'group') {
            const first = row.group.projects[0];
            return (
              <RowActions
                label={`Actions for ${row.group.name}`}
                size="sm"
                className="text-ink-4"
                items={[
                  {
                    label: row.expanded ? `Hide the ${row.info.count} projects` : `Show the ${row.info.count} projects`,
                    icon: row.expanded ? ChevronDown : ChevronRight,
                    onSelect: () => onToggleGroup(row.group.key),
                  },
                  selectable && { label: `Select all ${row.info.count}`, icon: ListChecks, onSelect: () => onSelectGroup(row.group) },
                  canEdit && { label: 'Add another system…', icon: CopyPlus, onSelect: () => onDuplicate(first) },
                ]}
                data-testid="project-group-actions"
              />
            );
          }
          return (
            <RowActions
              label={`Actions for ${row.project.name}`}
              items={menuFor(row.project)}
              size="sm"
              className="text-ink-4"
              data-testid={`project-actions-${row.project.id}`}
            />
          );
        },
      },
    ];
  }, [navigate, canEdit, canDelete, onEdit, onDuplicate, onDelete, onCopyLink, onToggleGroup, onSelectGroup, selectable, selected, now, medium]);

  // "E" on a project row focused with Tab / the arrow keys opens Edit details (the ⋯ menu shows the shortcut).
  // The table's own keys (arrows, Enter, Space) stay with the table; a focused link or button inside the row
  // handles its own keys. Site rows have no edit panel.
  const onKeyDown = (event) => {
    if (!canEdit || !onEdit || event.defaultPrevented) return;
    if (event.key !== 'e' && event.key !== 'E') return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.nativeEvent?.isComposing) return;
    const target = event.target;
    if (target?.tagName !== 'TR') return;
    const testId = target.getAttribute('data-testid') || '';
    if (!testId.startsWith('project-card-')) return;
    const row = (rows || []).find((item) => item.kind !== 'group' && `project-card-${item.project.id}` === testId);
    if (!row) return;
    event.preventDefault();
    onEdit(row.project);
  };

  return (
    // Not a widget of its own: it only listens for the rows' "E" shortcut as their key events bubble up.
    <div onKeyDown={onKeyDown}>
      <DataTable
        testId="projects-list"
        ariaLabel="Projects"
        className={className}
        tableClassName="table-fixed"
        columns={columns}
        rows={rows}
        rowKey="key"
        loading={loading}
        skeletonRows={10}
        empty={empty}
        sort={sort}
        onSortChange={onSortChange}
        manualSort
        selectable={selectable}
        selectedKeys={selectable ? selectedKeys : undefined}
        onSelectionChange={onSelectionChange}
        // The page prunes the selection itself: a collapsed group keeps its (hidden) projects selected.
        keepSelectionOnFilter
        getRowLabel={(row) => (row.kind === 'group'
          ? `all ${row.info.count} projects of ${row.group.name}`
          : `${row.project.name}${row.project.system_type ? ` (${row.project.system_type})` : ''}`)}
        onRowOpen={(row) => {
          if (row.kind === 'group') onToggleGroup(row.group.key);
          else navigate(`/projects/${encodeURIComponent(row.project.id)}`);
        }}
        rowTestId={(row) => (row.kind === 'group' ? `project-group-${slug(row.group.name)}` : `project-card-${row.project.id}`)}
        rowClassName={(row) => cn(row.kind === 'group' && 'font-normal', row.kind === 'child' && 'text-[12.5px]')}
        initialRowCount={150}
        pagingKey={pagingKey}
        renderBulkBar={renderBulkBar}
      />
    </div>
  );
}

export default ProjectsTable;
